import moment from 'moment';
import { goalDataTypeEnums, trialStatusEnums } from 'utils';

/**
 * Manual session data entry (Client → Goals → Add Data).
 *
 * Trials are held in local state as the user types them and sent in one PUT on Save — nothing is
 * written per click, unlike the live session screen. Times are wall-clock `HH:mm:ss` strings in
 * the browser's zone while editing, and become ISO datetimes on the session's date when sent.
 * Backend contract: docs/modules/clinical.md → "Manual session data entry".
 */

export const MANUAL_SESSION_APPTS_ACTION = 'GET_MANUAL_SESSION_APPTS';
export const MANUAL_SESSION_LOAD_ACTION = 'GET_MANUAL_SESSION';
export const MANUAL_SESSION_SAVE_ACTION = 'SAVE_MANUAL_SESSION';
export const GOALS_FOR_SESSION_ACTION = 'GET_GOALS_FOR_SESSION';

export const MANUAL_SESSION_ORIGIN = 'MANUAL';

/** Query keys of the data-entry route. Ids only — nothing identifying goes in the URL. */
export const manualQueryKeys = {
    DATE: 'date',
    TIME: 'time',
    END_TIME: 'end',
    APPT: 'appt',
    SESSION: 'session',
};

export const manualSessionPath = (clientId, { date, time, endTime, apptId, sessionId }) => {
    const query = new URLSearchParams();
    query.set(manualQueryKeys.DATE, date);
    if (time) query.set(manualQueryKeys.TIME, time);
    if (endTime) query.set(manualQueryKeys.END_TIME, endTime);
    if (apptId) query.set(manualQueryKeys.APPT, apptId);
    if (sessionId) query.set(manualQueryKeys.SESSION, sessionId);
    return `/client/${clientId}/manual-data?${query.toString()}`;
};

export const DATE_FORMAT = 'YYYY-MM-DD';
export const TIME_FORMAT = 'HH:mm:ss';

const INTERVAL_TYPES = [
    goalDataTypeEnums.WHOLE_INTERVAL,
    goalDataTypeEnums.PARTIAL_INTERVAL,
    goalDataTypeEnums.MOMENTARY_TIME_SAMPLING,
];

export const isIntervalType = (dataType) => INTERVAL_TYPES.includes(dataType);

/** Types whose trials carry a start and an end time. DURATION / IRT / LATENCY derive their value from them. */
export const TIMED_TYPES = [goalDataTypeEnums.DURATION, goalDataTypeEnums.IRT, goalDataTypeEnums.LATENCY, goalDataTypeEnums.RATE];
export const isTimedType = (dataType) => TIMED_TYPES.includes(dataType);

/** FREQUENCY is one trial holding the whole count; every other type is a list of trials. */
export const isSingleTrialType = (dataType) => dataType === goalDataTypeEnums.FREQUENCY;

/** One-click types move to the next empty trial as soon as a value is picked. */
export const isAutoAdvanceType = (dataType) =>
    dataType === goalDataTypeEnums.PERCENT ||
    dataType === goalDataTypeEnums.RATING ||
    dataType === goalDataTypeEnums.SCORE ||
    isIntervalType(dataType);

export const LATENCY_LABELS = { CORRECT: 'CORRECT', INCORRECT: 'INCORRECT', NO_RESPONSE: 'NO_RESPONSE' };

export const SCORE_MIN = 1;
export const SCORE_MAX = 99;

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** Seconds from `start` to `end` (both `HH:mm:ss`), or null when either is missing. */
export const secondsBetween = (start, end) => {
    if (!start || !end) return null;
    const s = moment(start, TIME_FORMAT);
    const e = moment(end, TIME_FORMAT);
    if (!s.isValid() || !e.isValid()) return null;
    return e.diff(s, 'seconds');
};

export const formatSeconds = (total) => {
    if (!isNum(total) || total < 0) return '--:--';
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

/** `date` (YYYY-MM-DD) + `time` (HH:mm:ss), read in the browser's zone, as an ISO datetime. */
export const toIsoDateTime = (date, time) => moment(`${date} ${time}`, `${DATE_FORMAT} ${TIME_FORMAT}`).toISOString();

export const toLocalTime = (iso) => (iso ? moment(iso).format(TIME_FORMAT) : null);

let localTrialSeq = 0;
/** Local id so the shared session widgets (TrialCircles, `current`) can tell trials apart. Never sent. */
export const newTrialId = () => `local-${++localTrialSeq}`;

export const emptyTrial = () => ({ id: null, label: null, value: null, startTime: null, endTime: null });

/** Whether a trial holds everything the backend needs for its data type. */
export const isTrialComplete = (dataType, t) => {
    if (!t) return false;
    switch (dataType) {
        case goalDataTypeEnums.PERCENT:
        case goalDataTypeEnums.RATING:
            return !!t.label && isNum(t.value);
        case goalDataTypeEnums.SCORE:
            return isNum(t.value) && t.value >= SCORE_MIN && t.value <= SCORE_MAX;
        case goalDataTypeEnums.FREQUENCY:
            return Number.isInteger(t.value) && t.value >= 0;
        case goalDataTypeEnums.RATE:
            return !!t.startTime && !!t.endTime && Number.isInteger(t.value) && t.value >= 0;
        case goalDataTypeEnums.DURATION:
        case goalDataTypeEnums.IRT:
            return !!t.startTime && !!t.endTime;
        case goalDataTypeEnums.LATENCY:
            return !!t.startTime && !!t.endTime && !!t.label;
        default:
            return isIntervalType(dataType) ? t.value === 0 || t.value === 1 : false;
    }
};

// Mirrors the goal option's `timeType` (TimeType in the package; the backend spells it SECUND).
const OPTION_TIME_TYPES = { MINUTE: 'MINUTE', SECUND: 'SECUND' };

/**
 * A LATENCY goal's "Expired in" window: `options[0]`, whose `value` is always stored in seconds —
 * `timeType` only says whether the goal editor shows it in minutes or seconds. The backend
 * (`checkTrialValue`) rejects a latency of 0 or above it. Null for other types or a goal without one.
 */
export const latencyLimit = (goal) => {
    if (goal?.dataType !== goalDataTypeEnums.LATENCY) return null;
    const option = goal?.options?.[0];
    if (!isNum(option?.value) || option.value <= 0) return null;
    const label =
        option.timeType === OPTION_TIME_TYPES.MINUTE ? `${Number((option.value / 60).toFixed(2))} min` : `${option.value} sec`;
    return { seconds: option.value, label };
};

/** A latency trial's start → end window against the goal's limit: 'zero', 'over' or null when fine. */
const latencyWindowProblem = (goal, t) => {
    const secs = secondsBetween(t?.startTime, t?.endTime);
    if (secs === null || secs < 0) return null;
    if (secs === 0) return 'zero';
    const limit = latencyLimit(goal);
    return limit && secs > limit.seconds ? 'over' : null;
};

/** A trial the user started but left unusable, or one the backend would reject. Null when fine. */
export const trialProblem = (goal, t) => {
    const dataType = goal?.dataType;
    if (!isTrialComplete(dataType, t)) {
        if (dataType === goalDataTypeEnums.SCORE && isNum(t?.value)) return `Score must be between ${SCORE_MIN} and ${SCORE_MAX}.`;
        if (isTimedType(dataType)) return 'Needs a start time, an end time' + (dataType === goalDataTypeEnums.RATE ? ' and a count.' : dataType === goalDataTypeEnums.LATENCY ? ' and a response.' : '.');
        return 'Is incomplete.';
    }
    if (isTimedType(dataType)) {
        const secs = secondsBetween(t.startTime, t.endTime);
        if (secs < 0) return 'End time is before its start time.';
        if (dataType === goalDataTypeEnums.RATE && t.value > 0 && secs === 0) return 'Needs an observation window longer than zero.';
        if (dataType === goalDataTypeEnums.LATENCY) {
            const latency = latencyWindowProblem(goal, t);
            if (latency === 'zero') return 'Latency must be longer than zero.';
            if (latency === 'over') return `Latency can't be longer than the goal's ${latencyLimit(goal).label} limit.`;
        }
    }
    return null;
};

/** The inputs to mark red for a trial `trialProblem` rejects. Empty when the problem isn't tied to one field. */
export const trialErrorFields = (goal, t) => {
    const dataType = goal?.dataType;
    if (!isTimedType(dataType) || !t) return [];
    const fields = [];
    if (!t.startTime) fields.push('startTime');
    if (!t.endTime) fields.push('endTime');
    if (!fields.length) {
        const secs = secondsBetween(t.startTime, t.endTime);
        if (secs < 0) fields.push('endTime');
        else if (dataType === goalDataTypeEnums.RATE && t.value > 0 && secs === 0) fields.push('startTime', 'endTime');
        else if (dataType === goalDataTypeEnums.LATENCY && latencyWindowProblem(goal, t)) fields.push('endTime');
    }
    if (dataType === goalDataTypeEnums.RATE && !Number.isInteger(t.value)) fields.push('value');
    return fields;
};

/** Trials of a loaded session's tSummary, in the local editing shape. Live IN_PROGRESS rows never exist on a manual session. */
export const trialsFromTSummary = (tSummary) =>
    (tSummary?.trials || [])
        .filter((t) => t?.status !== trialStatusEnums.IN_PROGRESS)
        .map((t) => ({
            id: t?.id || newTrialId(),
            label: t?.label ?? null,
            value: isNum(t?.value) ? t.value : null,
            startTime: toLocalTime(t?.startTime),
            endTime: toLocalTime(t?.endTime),
        }));

/** One trial in the `PUT /clinical/sessions/manual` shape — only the fields its data type takes. */
export const toPayloadTrial = (dataType, t, date) => {
    const times = { startTime: toIsoDateTime(date, t.startTime), endTime: toIsoDateTime(date, t.endTime) };
    switch (dataType) {
        case goalDataTypeEnums.PERCENT:
        case goalDataTypeEnums.RATING:
            return { value: t.value, label: t.label };
        case goalDataTypeEnums.RATE:
            return { value: t.value, ...times };
        case goalDataTypeEnums.DURATION:
        case goalDataTypeEnums.IRT:
            return times;
        case goalDataTypeEnums.LATENCY:
            return { ...times, label: t.label };
        default:
            // SCORE, FREQUENCY and the interval types (0 / 1).
            return { value: t.value };
    }
};

const sameTrials = (a = [], b = []) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The `data` array for the save. Only targets whose trials changed are sent: each one sent REPLACES
 * what the session holds for it (`trials: []` removes it), and unsent targets are left untouched.
 */
export const buildManualPayloadData = (goals, entries, initial, date) => {
    const data = [];
    goals.forEach((goal) => {
        (goal?.targets || []).forEach((target) => {
            const trials = entries[target.id] || [];
            if (sameTrials(trials, initial[target.id] || [])) return;
            data.push({
                goalId: goal.id,
                targetId: target.id,
                trials: trials.map((t) => toPayloadTrial(goal.dataType, t, date)),
            });
        });
    });
    return data;
};

/** First problem across every edited target, as `{ targetId, message }`, or null. */
export const findEntryProblem = (goals, entries) => {
    for (const goal of goals) {
        for (const target of goal?.targets || []) {
            const trials = entries[target.id] || [];
            for (let i = 0; i < trials.length; i++) {
                const problem = trialProblem(goal, trials[i]);
                if (problem) {
                    // `explain`: every field is filled but a value is wrong (latency over the limit, end
                    // before start) — a red input alone doesn't say why, so the message is shown too.
                    return { targetId: target.id, trialIndex: i, fields: trialErrorFields(goal, trials[i]), explain: isTrialComplete(goal.dataType, trials[i]), message: `${target.name || 'Target'} — trial ${i + 1}: ${problem}` };
                }
            }
        }
    }
    return null;
};

export const hasEntryChanges = (entries, initial) => {
    const ids = new Set([...Object.keys(entries), ...Object.keys(initial)]);
    for (const id of ids) {
        if (!sameTrials(entries[id] || [], initial[id] || [])) return true;
    }
    return false;
};

/** An appointment suggestion's `hh:mm A – hh:mm A` window. */
export const apptTimeRange = (appt) =>
    appt?.startTime ? `${moment(appt.startTime).format('hh:mm A')} – ${moment(appt.endTime).format('hh:mm A')}` : '';

/** "0s", "45s", "2m 5s", "1h 3m" — the short form the design uses for the target's running value. */
export const formatShortSeconds = (total) => {
    if (!isNum(total) || total < 0) return '0s';
    const secs = Math.round(total);
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h) return m ? `${h}h ${m}m` : `${h}h`;
    if (m) return s ? `${m}m ${s}s` : `${m}m`;
    return `${s}s`;
};

// Mirrors the backend's TIME_UNIT_TO_SECONDS (not exposed to the frontend through the package).
const TIME_UNIT_SECONDS = { SECOND: 1, MINUTE: 60, HOUR: 3600 };

const trialSeconds = (t) => Math.max(0, secondsBetween(t?.startTime, t?.endTime) || 0);

/**
 * The target's value over the trials typed so far, as the backend will compute it on save
 * (`CalculateTrialValue`): % of 1s, rating/score average, total count or time, IRT average
 * (first trial excluded), RATE per the goal's time unit. Only complete trials count. Null with none.
 */
export const manualTrialSummary = (goal, trials = []) => {
    const dataType = goal?.dataType;
    const done = trials.filter((t) => isTrialComplete(dataType, t));
    if (!done.length) return null;
    const total = done.reduce((sum, t) => sum + (t.value || 0), 0);
    const avg = total / done.length;

    switch (dataType) {
        case goalDataTypeEnums.PERCENT:
            return `${Math.round(avg * 100)}%`;
        case goalDataTypeEnums.RATING:
            return avg.toFixed(1);
        case goalDataTypeEnums.SCORE:
            return `${Number(avg.toFixed(2))}`;
        case goalDataTypeEnums.FREQUENCY:
            return `${total}`;
        case goalDataTypeEnums.DURATION:
        case goalDataTypeEnums.LATENCY:
            return formatShortSeconds(done.reduce((sum, t) => sum + trialSeconds(t), 0));
        case goalDataTypeEnums.IRT: {
            const rest = done.slice(1);
            return rest.length ? formatShortSeconds(rest.reduce((sum, t) => sum + trialSeconds(t), 0) / rest.length) : '0s';
        }
        case goalDataTypeEnums.RATE: {
            const unit = goal?.timeUnit && TIME_UNIT_SECONDS[goal.timeUnit] ? goal.timeUnit : 'MINUTE';
            const secs = done.reduce((sum, t) => sum + trialSeconds(t), 0);
            const rate = secs > 0 ? Number(((total / secs) * TIME_UNIT_SECONDS[unit]).toFixed(2)) : 0;
            return `${rate}/${unit.charAt(0).toLowerCase()}`;
        }
        default:
            return isIntervalType(dataType) ? `${Math.round(avg * 100)}%` : null;
    }
};
